import { useQueryClient } from '@tanstack/react-query'
import { EyeIcon, XIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useUser } from '@/api/user/queries/useUser'
import { Button } from '@/components/ui/button'
import { releaseImpersonation, useImpersonatedUserId } from '@/lib/impersonation'
import { cn } from '@/lib/utils'
import { OfficeRoutePath } from '@/RoutePath'

type Props = {
  className?: string
}

export function ImpersonationBanner({ className }: Props) {
  const impersonatedUserId = useImpersonatedUserId()
  const { data: targetUser } = useUser(impersonatedUserId ?? '')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  if (!impersonatedUserId) {
    return null
  }

  const displayName =
    targetUser?.name && targetUser?.familyName ? `${targetUser.name} ${targetUser.familyName}` : (targetUser?.email ?? impersonatedUserId)

  const handleExit = async () => {
    const userId = impersonatedUserId
    await releaseImpersonation(queryClient)
    navigate(`${OfficeRoutePath.UserPage}/${userId}`)
  }

  return (
    <div
      className={cn(
        'border-border-default flex flex-wrap items-center justify-between gap-3 border-b bg-amber-50 px-4 py-2 text-sm text-amber-950',
        className
      )}
    >
      <Button type='button' variant='outline' size='sm' onClick={handleExit}>
        <XIcon className='size-4' />
      </Button>
      <div className='flex items-center gap-2'>
        <EyeIcon className='size-4 shrink-0' />
        <span>
          Viewing as <strong>{displayName}</strong>
        </span>
      </div>
    </div>
  )
}
